import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import { format, parse, isValid } from 'date-fns';
import { getPerthToday } from '../utils/dateUtils';
import { CalendarDays, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import 'react-day-picker/dist/style.css';

// Custom styles for the calendar
const calendarStyles = `
  .pmp-calendar .rdp {
    --rdp-cell-size: 36px;
    --rdp-accent-color: #e97a20;
    --rdp-background-color: #fef3e8;
    margin: 0;
  }
  .pmp-calendar .rdp-months { justify-content: center; }
  .pmp-calendar .rdp-month { width: 100%; }
  .pmp-calendar .rdp-caption { display: flex; align-items: center; justify-content: space-between; padding: 0 4px 8px; }
  .pmp-calendar .rdp-caption_label { font-size: 0.875rem; font-weight: 600; color: #1e293b; }
  .pmp-calendar .rdp-nav { display: flex; gap: 2px; }
  .pmp-calendar .rdp-nav_button { width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; border-radius: 6px; background: transparent; border: none; cursor: pointer; color: #64748b; transition: all 0.15s; }
  .pmp-calendar .rdp-nav_button:hover { background: #f1f5f9; color: #1e293b; }
  .pmp-calendar .rdp-table { width: 100%; border-collapse: collapse; }
  .pmp-calendar .rdp-head_cell { font-size: 0.75rem; font-weight: 500; color: #94a3b8; text-transform: uppercase; padding: 4px 0; }
  .pmp-calendar .rdp-cell { text-align: center; padding: 1px; }
  .pmp-calendar .rdp-day { width: 36px; height: 36px; font-size: 0.8125rem; border-radius: 8px; border: none; background: transparent; cursor: pointer; color: #334155; transition: all 0.15s; }
  .pmp-calendar .rdp-day:hover:not(.rdp-day_selected):not(.rdp-day_outside) { background: #f1f5f9; }
  .pmp-calendar .rdp-day_selected { background: #e97a20 !important; color: white !important; font-weight: 600; }
  .pmp-calendar .rdp-day_today:not(.rdp-day_selected) { font-weight: 700; color: #e97a20; background: #fef3e8; }
  .pmp-calendar .rdp-day_outside { color: #cbd5e1; }
`;

export function DatePicker({ value, onChange, className = '', placeholder = 'Select date', testId = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const selected = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined;
  const displayValue = selected && isValid(selected) ? format(selected, 'dd MMM yyyy') : '';

  const updatePosition = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 340;
      const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
      setDropdownPos({
        top: openAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left: Math.min(rect.left, window.innerWidth - 290),
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest('.pmp-calendar-portal')) setOpen(false);
    };
    if (open) {
      updatePosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const handleSelect = (date) => {
    if (date) {
      onChange(format(date, 'yyyy-MM-dd'));
    }
    setOpen(false);
  };

  return (
    <>
      <style>{calendarStyles}</style>
      <div className="relative" ref={ref}>
        <div
          className={`flex items-center w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white cursor-pointer hover:border-slate-400 transition-colors ${open ? 'border-orange-400 ring-2 ring-orange-100' : ''} ${className}`}
          onClick={() => setOpen(!open)}
          data-testid={testId}
        >
          <CalendarDays size={15} className="text-slate-400 mr-2 flex-shrink-0" />
          <span className={displayValue ? 'text-slate-800' : 'text-slate-400'}>{displayValue || placeholder}</span>
        </div>
        {open && createPortal(
          <div className="pmp-calendar-portal pmp-calendar" style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999, minWidth: '280px' }}>
            <div className="bg-white rounded-xl shadow-lg shadow-slate-200/80 border border-slate-200 p-3">
              <DayPicker
                mode="single"
                selected={selected}
                onSelect={handleSelect}
                defaultMonth={selected || new Date()}
                showOutsideDays
                components={{
                  IconLeft: () => <ChevronLeft size={16} />,
                  IconRight: () => <ChevronRight size={16} />,
                }}
              />
              <div className="border-t border-slate-100 pt-2 mt-1 flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => { onChange(getPerthToday()); setOpen(false); }}
                  className="text-xs text-orange-600 hover:text-orange-700 font-medium px-2 py-1 rounded hover:bg-orange-50 transition-colors"
                >
                  Today
                </button>
                {value && (
                  <button
                    type="button"
                    onClick={() => { onChange(''); setOpen(false); }}
                    className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1 rounded hover:bg-slate-50 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </>
  );
}

export function TimePicker({ value, onChange, className = '', placeholder = 'hh:mm', testId = '' }) {
  // value is stored as 24-hour "HH:MM" (e.g. "14:05"). We display 12-hour hh:mm + separate AM/PM.
  const inputRef = useRef(null);

  // Derive current 12-hour parts from the stored 24-hour value
  const to12h = (val) => {
    if (!val || !/^\d{1,2}:\d{2}$/.test(val)) return { text: '', ampm: 'AM' };
    const [hStr, mStr] = val.split(':');
    const h24 = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (Number.isNaN(h24) || Number.isNaN(m) || h24 < 0 || h24 > 23 || m < 0 || m > 59) return { text: '', ampm: 'AM' };
    const ampm = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
    return { text: `${h12}:${String(m).padStart(2, '0')}`, ampm };
  };

  const current = to12h(value);
  const [text, setText] = useState(current.text);
  const [ampm, setAmpm] = useState(current.ampm);
  const [isEditing, setIsEditing] = useState(false);
  const [invalid, setInvalid] = useState(false);

  // Sync external value changes into local state when not editing
  useEffect(() => {
    if (!isEditing) {
      const next = to12h(value);
      setText(next.text);
      setAmpm(next.ampm);
      setInvalid(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Parse "h", "hh", "h:mm", "hh:mm" in 12-hour format -> {h12, m} or null
  const parse12h = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    const match = s.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
    if (!match) return null;
    const h12 = parseInt(match[1], 10);
    const m = match[2] !== undefined ? parseInt(match[2], 10) : 0;
    if (Number.isNaN(h12) || Number.isNaN(m)) return null;
    if (h12 < 1 || h12 > 12) return null;
    if (m < 0 || m > 59) return null;
    return { h12, m };
  };

  // Auto-insert colon as the user types. Rules (12-hour):
  //  1 digit            -> "H"              (e.g. "5")
  //  2 digits 10/11/12  -> "HH"             (still a valid 2-digit hour, wait for more)
  //  2 digits otherwise -> "H:M"            (auto-colon, 2nd digit becomes first minute)
  //  3 digits with hh=10/11/12 -> "HH:M"
  //  3 digits otherwise -> "H:MM"
  //  4 digits with hh=10/11/12 -> "HH:MM"
  //  4 digits otherwise -> "H:MM" (ignore 4th)
  const formatAsTyping = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 4);
    if (digits.length === 0) return '';
    if (digits.length === 1) return digits;
    const twoDigitHour = parseInt(digits.slice(0, 2), 10);
    const hourIsTwoDigit = twoDigitHour >= 10 && twoDigitHour <= 12;
    if (digits.length === 2) return hourIsTwoDigit ? digits : `${digits[0]}:${digits[1]}`;
    if (digits.length === 3) return hourIsTwoDigit ? `${digits.slice(0, 2)}:${digits[2]}` : `${digits[0]}:${digits.slice(1)}`;
    // 4 digits
    return hourIsTwoDigit ? `${digits.slice(0, 2)}:${digits.slice(2, 4)}` : `${digits[0]}:${digits.slice(1, 3)}`;
  };

  const commit = (nextText, nextAmpm) => {
    const t = nextText.trim();
    if (t === '') {
      onChange('');
      setInvalid(false);
      return true;
    }
    const parsed = parse12h(t);
    if (!parsed) {
      setInvalid(true);
      return false;
    }
    let h24 = parsed.h12;
    if (nextAmpm === 'PM' && h24 !== 12) h24 += 12;
    if (nextAmpm === 'AM' && h24 === 12) h24 = 0;
    const normalized24 = `${String(h24).padStart(2, '0')}:${String(parsed.m).padStart(2, '0')}`;
    onChange(normalized24);
    // Normalize displayed text too (e.g., "9" -> "9:00", "09:5" -> "9:05")
    setText(`${parsed.h12}:${String(parsed.m).padStart(2, '0')}`);
    setInvalid(false);
    return true;
  };

  const handleBlur = () => {
    setIsEditing(false);
    commit(text, ampm);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.blur(); }
    else if (e.key === 'Escape') { setIsEditing(false); const c = to12h(value); setText(c.text); setAmpm(c.ampm); setInvalid(false); inputRef.current?.blur(); }
  };

  const toggleAmpm = () => {
    const next = ampm === 'AM' ? 'PM' : 'AM';
    setAmpm(next);
    // If there's a valid time typed, re-emit with new AM/PM immediately
    if (text.trim() && parse12h(text)) {
      commit(text, next);
    }
  };

  return (
    <div className="relative">
      <div
        className={`flex items-center w-full pl-3 pr-1 border rounded-lg text-sm bg-white transition-colors ${invalid ? 'border-red-400 ring-2 ring-red-100' : isEditing ? 'border-orange-400 ring-2 ring-orange-100' : 'border-slate-300 hover:border-slate-400'} ${className}`}
      >
        <Clock size={15} className="text-slate-400 mr-2 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={text}
          placeholder={placeholder}
          maxLength={5}
          onFocus={(e) => { setIsEditing(true); setTimeout(() => e.target.select(), 0); }}
          onChange={(e) => { setText(formatAsTyping(e.target.value)); setInvalid(false); }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="flex-1 py-2 bg-transparent outline-none text-slate-800 placeholder-slate-400 min-w-0 w-full"
          data-testid={testId}
          aria-invalid={invalid}
        />
        <button
          type="button"
          onClick={toggleAmpm}
          className={`ml-1 px-2 py-1 text-xs font-semibold rounded select-none flex-shrink-0 transition-colors ${ampm === 'PM' ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          data-testid={testId ? `${testId}-ampm` : undefined}
          aria-label={`Toggle AM/PM, currently ${ampm}`}
        >
          {ampm}
        </button>
      </div>
      {invalid && (
        <div className="text-xs text-red-600 mt-1" data-testid={testId ? `${testId}-error` : undefined}>
          Invalid time. Use hh:mm (1–12 hours, 0–59 minutes).
        </div>
      )}
    </div>
  );
}
